"use client";

import { useEffect, useState } from "react";
import { Menu, Search, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetClose } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        {[0, 1, 2, 3].map(i => <div key={i} data-index={i} className="h-3 w-16 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative text-foreground bg-transparent hover:bg-accent focus:bg-transparent px-0 py-2 text-[12px] font-default font-normal uppercase tracking-[0.2em] whitespace-nowrap">
                <span className="relative inline-block">
                  {item.label}
                  <span className="absolute -bottom-1 left-0 h-px w-0 bg-primary transition-all duration-300 ease-out group-hover:w-full" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6">
        {[0, 1, 2, 3].map(i => <div key={i} data-index={i} className="h-10 w-3/4 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-6">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onItemClick} className="group flex items-baseline gap-4 text-foreground">
          <span className="font-mono text-xs text-primary tracking-widest pt-2">
            {String(i + 1).padStart(2, "0")}
          </span>
          <span className="text-foreground font-default text-4xl sm:text-5xl font-medium uppercase tracking-tight leading-none transition-colors group-hover:text-primary">
            {item.label}
          </span>
        </Link>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="4282" id="header" className={`sticky top-0 z-50 w-full bg-background text-foreground transition-all duration-300 ${scrolled ? "bg-background/80 backdrop-blur-md shadow-sm border-b border-border" : "border-b border-border"}`}>
      <div className="mx-auto flex py-5 max-w-7xl items-center justify-between px-6 lg:px-10 lg:grid lg:grid-cols-[auto_1fr_auto] lg:items-center lg:w-full lg:gap-x-8">
        {}
        <div className="flex items-center lg:col-start-1 lg:justify-self-start whitespace-nowrap">
          <Link to="/" className="flex items-center gap-2 lg:gap-3">
            <div className="h-6 border-l border-border/40" />
            <img src="https://ardacansahin.av.tr/wp-content/uploads/2026/07/ChatGPT-Image-5-Tem-2026-12_47_17-1.png" data-wvc-srcset="https://ardacansahin.av.tr/wp-content/uploads/2026/07/ChatGPT-Image-5-Tem-2026-12_47_17-1.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695387/img/ChatGPT Image 5 Tem 2026 12_47_17.png-wvc-sizes" className="h-10 sm:h-12 w-auto max-w-[32px] sm:max-w-[42px] lg:max-w-none" alt="Av. Arda Can Şahin Logo" loading="lazy" />
            <div className="flex flex-col">
              <span className="text-sm sm:text-lg lg:text-2xl font-semibold tracking-[0.3px] text-[#FFFFFF] hover:text-[#FFFFFF] whitespace-nowrap leading-none inline-flex" style={{
              fontFamily: 'Playfair Display, serif'
            }}>
                Av. Arda Can Şahin
              </span>
              <span className="inline text-[7px] sm:text-[10px] uppercase font-sans tracking-[2px] text-[#C9A15A] mt-1 leading-none">
                HUKUK & DANIŞMANLIK
              </span>
            </div>
            <div className="h-6 border-r border-border/40" />
          </Link>
        </div>

        {}
        <div className="hidden lg:flex lg:col-start-2 lg:justify-self-end lg:items-center lg:gap-7 whitespace-nowrap">
          <MenuProvider menu_id="31">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center gap-2 sm:gap-4 flex-1 lg:flex-none justify-end lg:col-start-3 lg:justify-self-end">
          <Button variant="ghost" size="icon" className="hidden sm:inline-flex text-foreground hover:bg-accent hover:text-accent-foreground rounded-md" aria-label="Search">
            <Search className="size-[18px]" strokeWidth={1.5} />
          </Button>

          {}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden text-foreground hover:bg-accent hover:text-accent-foreground rounded-md" aria-label="Open menu">
                <Menu className="size-5" strokeWidth={1.5} />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-full bg-background text-foreground border-l-0 p-0">
              <SheetTitle className="sr-only">Navigasyon Menüsü</SheetTitle>

              <div className="flex h-24 items-center justify-between px-6 border-b border-border">
                <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 lg:gap-3">
                  <div className="h-6 border-l border-border/40" />
                  <img src="https://ardacansahin.av.tr/wp-content/uploads/2026/07/ChatGPT-Image-5-Tem-2026-12_47_17-1.png" data-wvc-srcset="https://ardacansahin.av.tr/wp-content/uploads/2026/07/ChatGPT-Image-5-Tem-2026-12_47_17-1.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695387/img/ChatGPT Image 5 Tem 2026 12_47_17.png-wvc-sizes" className="h-10 sm:h-12 w-auto max-w-[36px] sm:max-w-[42px] lg:max-w-none" alt="Av. Arda Can Şahin Logo" loading="lazy" />
                  <div className="flex flex-col">
                    <span className="text-sm sm:text-lg lg:text-2xl font-semibold tracking-[0.2px] text-[#FFFFFF] hover:text-[#FFFFFF] whitespace-nowrap leading-none inline-flex" style={{
                    fontFamily: 'Playfair Display, serif'
                  }}>
                      Av. Arda Can Şahin
                    </span>
                    <span className="block text-[7px] sm:text-[10px] uppercase font-sans tracking-[2px] text-[#C9A15A] mt-1 leading-none">
                      HUKUK & DANIŞMANLIK
                    </span>
                  </div>
                  <div className="h-6 border-r border-border/40" />
                </Link>
                <SheetClose asChild>
                  <Button variant="ghost" size="icon" className="text-foreground hover:bg-accent hover:text-accent-foreground rounded-md" aria-label="Close menu">
                    <X className="size-5" strokeWidth={1.5} />
                  </Button>
                </SheetClose>
              </div>

              <div className="flex flex-col justify-between h-[calc(100vh-6rem)] px-6 py-10 overflow-y-auto">
                <MenuProvider menu_id="31">
                  <MobileMenuConsumer onItemClick={() => setMobileOpen(false)} />
                </MenuProvider>

                <div className="mt-10 flex flex-col gap-4 pt-8 border-t border-border">
                  <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                    STRATEJİK BAKIŞ — GÜÇLÜ TEMSİL
                  </p>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}